import React, { useState, useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect } from "react-redux";
import { setAlert } from "../../../actions/alert";
import { followCitySignup } from "../../../util/adminApi";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import {
  LeftDiv,
  SpaceApartDiv,
} from "../restaurantBuilder/restaurantBuilderLegacy/RestaurantCreateBuild";
import { RECAPTCHA_SITEKEY } from "../../../config";

const FollowCityWidget = ({ setAlert, currentLocation }) => {
  const [email, setEmail] = useState("");
  const [radiusMiles, setRadiusMiles] = useState(10);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Selected city data from Places Autocomplete
  const [selectedCity, setSelectedCity] = useState(null);

  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const placesLib = useMapsLibrary("places");

  // Initialize Google Places Autocomplete
  useEffect(function initGooglePlacesAutocomplete() {
    if (!placesLib || !inputRef.current || autocompleteRef.current) return;

    const autocomplete = new placesLib.Autocomplete(
      inputRef.current,
      {
        types: ["(cities)"],
        componentRestrictions: { country: "us" },
      }
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      // Extract city and state from address components
      let cityName = "";
      let state = "";
      for (const component of place.address_components || []) {
        if (component.types.includes("locality")) {
          cityName = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          state = component.short_name;
        }
      }

      setSelectedCity({ cityName, state, lat, lng });
    });

    autocompleteRef.current = autocomplete;

    // Pre-fill with detected city if available
    if (currentLocation?.locationDisplay?.name && inputRef.current) {
      inputRef.current.value = currentLocation.locationDisplay.name;
    }
  }, [placesLib]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recaptchaToken) {
      setAlert("Please complete the reCAPTCHA", "failure");
      return;
    }

    if (!selectedCity) {
      setAlert("Please select a city from the dropdown", "failure");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await followCitySignup({
        email,
        location: { lat: selectedCity.lat, lng: selectedCity.lng },
        city: selectedCity.cityName,
        recaptchaToken,
        preferences: {
          radiusMiles,
          cityName: selectedCity.cityName,
          state: selectedCity.state,
          lat: selectedCity.lat,
          lng: selectedCity.lng,
        },
      });

      if (resp?.response?.status === 400) {
        setAlert(
          resp.response.data.errors.map((obj) => obj.msg).join(", "),
          "failure"
        );
      } else if (resp?.response?.status >= 500) {
        setAlert(
          "Something went wrong. Please try again.",
          "failure"
        );
      } else {
        setSuccess(true);
      }
    } catch {
      setAlert("Something went wrong. Please try again.", "failure");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    const displayName = selectedCity?.state
      ? `${selectedCity.cityName}, ${selectedCity.state}`
      : selectedCity?.cityName;
    return (
      <div style={{ textAlign: "center", padding: "16px" }}>
        <i
          className="fas fa-check-circle"
          style={{ color: "var(--color-primary)", fontSize: "2em" }}
        ></i>
        <h3>You're following {displayName}!</h3>
        <p>We'll let you know when new vegan restaurants are added nearby.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-container">
        <LeftDiv>
          <SpaceApartDiv>
            <label className="form-label-inline">Email Address*</label>
            <input
              type="email"
              className="text-form"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </SpaceApartDiv>
          <SpaceApartDiv>
            <label className="form-label-inline">City</label>
            <input
              ref={inputRef}
              type="text"
              className="text-form"
              placeholder="Start typing a city name..."
              required
            />
          </SpaceApartDiv>
          {selectedCity && (
            <SpaceApartDiv>
              <label className="form-label-inline">Notify me within</label>
              <select
                className="text-form"
                value={radiusMiles}
                onChange={(e) => setRadiusMiles(Number(e.target.value))}
              >
                <option value={5}>5 miles</option>
                <option value={10}>10 miles</option>
                <option value={25}>25 miles</option>
              </select>
            </SpaceApartDiv>
          )}
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITEKEY}
            onChange={setRecaptchaToken}
          />
          <input
            style={{
              textAlign: "center",
              alignSelf: "center",
              fontSize: "1.5em",
              height: "revert",
            }}
            className="btn-primary"
            type="submit"
            value={submitting ? "Submitting..." : "Follow This City"}
            disabled={submitting}
          />
        </LeftDiv>
      </div>
    </form>
  );
};

const mapStateToProps = (state) => ({
  currentLocation: state.currentLocation,
});

export default connect(mapStateToProps, { setAlert })(FollowCityWidget);
